<template>
  <v-row class="mt-2">
    <v-col>
      <ValidationObserver ref="session">
        <v-expansion-panels multiple>
          <v-expansion-panel v-test="{ panel: 'tradeSession' }">
            <ValidationObserver ref="gavel" v-slot="{ failed }">
              <v-expansion-panel-header>
                <div id="gavel">
                  <v-icon>
                    gavel
                  </v-icon>
                  {{ $t('Торговая сессия (Закрытая)') }}
                  <v-icon v-if="failed" color="error">
                    warning
                  </v-icon>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content eager>
                <v-row v-if="sessionInfo.sessionNumber" class="pt-3">
                  <v-col
                      cols="12"
                      class="text-left pt-0 pl-1">
                    <text-view
                        :display="sessionInfo.sessionNumber"
                        :label="$t('Номер сессии')"
                        :no-data="$t('Не присвоен')" />
                  </v-col>
                </v-row>
                <v-row class="pt-3">
                  <v-col
                      cols="12"
                      md="6"
                      class="pl-4 pr-6 py-0">
                    <validatable-date-time-picker
                        v-model="session.dateStart"
                        v-test="{ date: 'dateStart' }"
                        :readonly="readonly"
                        :rules="{ required: true }"
                        :day-off-dates="dayOffDates"
                        :label="$t('Время начала торговой сессии')" />
                  </v-col>
                  <v-col
                      cols="12"
                      md="6"
                      class="pl-4 pr-6 py-0">
                    <validatable-date-time-picker
                        v-model="session.dateEnd"
                        v-test="{ date: 'dateEnd' }"
                        :readonly="readonly"
                        :rules="{ required: true,
                                  sessionEndDate: {
                                    startDate: session.dateStart
                                  } }"
                        :day-off-dates="dayOffDates"
                        :label="$t('Время завершения торговой сессии')" />
                  </v-col>
                </v-row>
                <v-row class="pt-3">
                  <v-col
                      cols="12"
                      md="6"
                      class="pl-2 py-0">
                    <validatable-enum-combobox
                        v-model="session.tradeDirectionType"
                        v-test="{ combobox: 'tradeDirectionType' }"
                        dense
                        :label="$t('Направление торгов')"
                        readonly
                        rules="required"
                        :values-enum="TRADE_DIRECTION_TYPE_ENUM" />
                  </v-col>
                  <v-col
                      cols="12"
                      md="6"
                      class="pl-2 py-0">
                    <v-checkbox
                        v-model="session.enableAdditionalOfferParams"
                        v-test="{ checkbox: 'enableAdditionalOfferParams' }"
                        :disabled="readonly"
                        :label="$t('Разрешать участникам загрузку файлов и дополнительных условий в офертах')" />
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </ValidationObserver>
          </v-expansion-panel>

          <v-expansion-panel v-if="isAdmin || isLotOwner(lot)" v-test="{ panel: 'invitedOrganizations' }">
            <ValidationObserver ref="invitedOrganizations" v-slot="{ failed }">
              <v-expansion-panel-header>
                <div id="invitedOrganizations">
                  <v-icon>
                    switch_account
                  </v-icon>
                  {{ $t('Приглашение участников') }}
                  <v-icon v-if="failed" color="error">
                    warning
                  </v-icon>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content eager>
                <v-row>
                  <v-col
                      cols="12"
                      class="pl-2 py-0">
                    <v-checkbox
                        v-model="showOrganizations"
                        v-test="{ checkbox: 'showOrganizations' }"
                        :disabled="readonly"
                        :label="$t('«Закрытые торги по составу участников»/«Ограничить участников»')" />
                  </v-col>
                  <v-col
                      v-if="showOrganizations"
                      cols="12"
                      class="pl-2 py-0">
                    <validatable-invited-organizations
                        v-model="session.invitedOrganizations"
                        v-test="{ combobox: 'invitedOrganizations' }"
                        :label="$t('Приглашенные организации')"
                        :search-label="$t('Выбор организации')"
                        :readonly="readonly"
                        :params="invitedOrganizationsParams"
                        @change="change" />
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </ValidationObserver>
          </v-expansion-panel>

          <v-expansion-panel v-if="showOrganizationsPrepayments" v-test="{ panel: 'organizationsPrepayments' }">
            <ValidationObserver ref="organizationsPrepayments" v-slot="{ failed }">
              <v-expansion-panel-header>
                <div id="organizationsPrepayments">
                  <v-icon>
                    mdi-cog-outline
                  </v-icon>
                  {{ $t('Размер предоплаты, %') }}
                  <v-icon v-if="failed" color="error">
                    warning
                  </v-icon>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content eager>
                <v-row>
                  <v-col
                      cols="12"
                      class="pl-2 py-1">
                    <validatable-organizations-prepayments
                        v-model="session.organizationsPrepayments"
                        :label="$t('Размер предоплаты, %')"
                        :readonly="readonly"
                        :params="invitedOrganizationsParams"
                        :loading="isLoading" />
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </ValidationObserver>
          </v-expansion-panel>
        </v-expansion-panels>
      </ValidationObserver>
    </v-col>
  </v-row>
</template>

<style scoped>

  .positions::v-deep .header-size, .positions::v-deep .cell-size {
    min-width: 220px!important;
  }
  .positions::v-deep .cell-size {
    padding-top: 10px!important;
  }
</style>

<script>
  import Vue from 'vue'
  // import ValidatableTextInput from '@/components/reused/ui/ValidatableTextInput.vue'
  import ValidatableEnumCombobox from '@/components/reused/ui/ValidatableEnumCombobox.vue'
  import ValidatableDateTimePicker from '@/components/reused/ui/ValidatableDateTimePicker.vue'
  import ValidatableInvitedOrganizations from '@/components/reused/ui/ValidatableInvitedOrganizations.vue'
  import ValidatableOrganizationsPrepayments from '@/components/reused/ui/ValidatableOrganizationsPrepayments.vue'
  import TextView from '@/components/reused/ui/TextView.vue'
  import { TRADE_DIRECTION_TYPE_ENUM } from '@/constants/enums'
  import { PARTICIPANT_TYPES, TRADE_DIRECTION_TYPES } from '@/constants/business'
  import { mapState } from 'vuex'
  import { CALENDAR } from '@/constants/store.js'
  export default {
    components: {
      // ValidatableTextInput,
      ValidatableEnumCombobox,
      ValidatableInvitedOrganizations,
      ValidatableOrganizationsPrepayments,
      ValidatableDateTimePicker,
      TextView
    },
    props: {
      readonly: {
        type: Boolean,
        default: false
      },
      sessionInfo: {
        type: Object,
        default: () => {
          return {}
        }
      },
      lot: {
        type: Object
      },
      create: {
        type: Boolean,
        default: false
      },
      validated: {
        type: [Function, Promise]
      }
    },
    data () {
      return {
        isLoading: false,
        TRADE_DIRECTION_TYPE_ENUM,
        itemsPerPage: -1,
        invitedOrganizationsParams: {
          participantType: PARTICIPANT_TYPES.PARTICIPANT
        },
        showOrganizations: false,
        session: {
          dateStart: null,
          dateEnd: null,
          tradeDirectionType: TRADE_DIRECTION_TYPES.RAISING,
          enableAdditionalOfferParams: null,
          invitedOrganizations: [],
          organizationsPrepayments: []
        }
      }
    },
    computed: {
      ...mapState(CALENDAR.NAMESPACE, { dayOffDays: CALENDAR.STATE.DAY_OFF_DAYS }),
      showOrganizationsPrepayments () {
        return this.isAdmin ||
          this.isLotOwner(this.lot) ||
          this.session.organizationsPrepayments.length
      },
      dayOffDates () {
        const years = this.dayOffDays
        const now = this.$moment()
        const thisYear = this.$moment().format('YYYY')
        const nextYear = now.add(1, 'years').format('YYYY')
        const thisYearDayOffDates = years[thisYear]
        const nextYearDayOffDates = years[nextYear]
        if (thisYearDayOffDates && nextYearDayOffDates) {
          return thisYearDayOffDates.concat(nextYearDayOffDates).map(d => this.$moment(d).format('YYYY-MM-DD'))
        }
        if (thisYearDayOffDates) {
          return thisYearDayOffDates.map(d => this.$moment(d).format('YYYY-MM-DD'))
        }
        if (nextYearDayOffDates) {
          return nextYearDayOffDates.map(d => this.$moment(d).format('YYYY-MM-DD'))
        }
        return null
      }
    },
    watch: {
      sessionInfo: {
        deep: true,
        immediate: true,
        handler: function (newVal) {
          this.mapFields(newVal)
        }
      }
    },
    created () {
      this.mapFields(this.sessionInfo)
    },
    methods: {
      validate: async function () {
        const isValid = await this.$refs.session.validate()
        if (!isValid) {
          throw new Vue.errorsTypes.ValidationError()
        }
        if (this.validated) {
          if (!this.showOrganizations) {
            this.session.invitedOrganizations = []
          }
          const session = {
            ...this.session
          }
          await this.validated(session)
        }
      },
      change: function (organizations) {
        const orgIds = organizations.map(o => o.organizationId)
        const organizationsPrepayments = this.session.organizationsPrepayments.filter(org => orgIds.includes(org.organizationId))
        organizations.forEach(o => {
          if (!organizationsPrepayments.some(op => op.organizationId === o.organizationId)) {
            organizationsPrepayments.push({
              organizationId: o.organizationId,
              prepaymentPercentage: o.prepaymentPercentage
            })
          }
        })
        this.session.organizationsPrepayments = organizationsPrepayments
      },
      mapFields: function (session) {
        this.isLoading = true
        this.session.dateStart = session.dateStart
        this.session.dateEnd = session.dateEnd
        this.session.tradeDirectionType = session.tradeDirectionType || TRADE_DIRECTION_TYPES.RAISING
        this.session.enableAdditionalOfferParams = session.enableAdditionalOfferParams
        this.session.invitedOrganizations = session.invitedOrganizations?.map(ps => ps) || []
        if (session.invitedOrganizations && session.invitedOrganizations.length) {
          this.showOrganizations = true
        }
        this.session.organizationsPrepayments = session.organizationsPrepayments || []
        this.isLoading = false
      }
    }
  }
</script>
